import { StorageLocationType } from "./schema/storage-location.js";
import { ReceivingMode } from "./wms-constants-enums.js";

export const WMS_RETURN_RESTOCK_FAILURE = "WMS RETURN RESTOCK FAILURE";

/**
 * Returns true if WMS Source of Truth is enabled for the given location.
 * - If `sourceOfTruthEnabled` is false, returns false for all locations.
 * - If `sourceOfTruthLocationIds` is empty/undefined, returns true for all locations (backwards compatible).
 * - Otherwise, returns true only if the location is in the list.
 */
export function isWmsSotEnabledForLocation(
  wms:
    | {
        sourceOfTruthEnabled?: boolean | null;
        sourceOfTruthLocationIds?: string[] | null;
      }
    | null
    | undefined,
  locationId: string | null | undefined,
): boolean {
  if (!wms?.sourceOfTruthEnabled) {
    return false;
  }
  const locationIds = wms.sourceOfTruthLocationIds;
  if (!locationIds?.length) {
    return true;
  }
  if (!locationId) {
    return false;
  }
  return locationIds.includes(locationId);
}

/**
 * Redo owns bin assignments at this location: the team prefers Redo bins and
 * WMS Source of Truth is off there.
 */
export function isRedoBinSourceOfTruth(
  wms:
    | {
        preferRedoBinsOverMarketplace?: boolean | null;
        sourceOfTruthEnabled?: boolean | null;
        sourceOfTruthLocationIds?: string[] | null;
      }
    | null
    | undefined,
  locationId: string | null | undefined,
): boolean {
  return (
    wms?.preferRedoBinsOverMarketplace === true &&
    !isWmsSotEnabledForLocation(wms, locationId)
  );
}

/** The Shopify variant metafield that holds a product's bin names. */
export function binMetafield(
  outboundLabels:
    | { binNamespace?: string | null; binKey?: string | null }
    | null
    | undefined,
): { namespace: string; key: string } {
  return {
    namespace: outboundLabels?.binNamespace || "custom",
    key: outboundLabels?.binKey || "bin",
  };
}

/**
 * The bin metafield, when Redo owns bins at any location and so is the only
 * writer of it. Other pushes to Shopify must neither set nor delete it.
 */
export function redoOwnedBinMetafield(
  outboundLabels:
    | {
        binNamespace?: string | null;
        binKey?: string | null;
        wms?: {
          preferRedoBinsOverMarketplace?: boolean | null;
          sourceOfTruthEnabled?: boolean | null;
          sourceOfTruthLocationIds?: string[] | null;
        } | null;
      }
    | null
    | undefined,
): { namespace: string; key: string } | undefined {
  const wms = outboundLabels?.wms;
  const wmsOwnsEveryLocation =
    wms?.sourceOfTruthEnabled === true && !wms.sourceOfTruthLocationIds?.length;
  return wms?.preferRedoBinsOverMarketplace === true && !wmsOwnsEveryLocation
    ? binMetafield(outboundLabels)
    : undefined;
}

/**
 * WMS SOT is on at all — globally, or for some subset of locations.
 *
 * Use when a capability has to be withheld team-wide because it cannot be
 * scoped to one location, rather than decided per location.
 */
export function isWmsSotEnabledForAnyLocation(
  wms: { sourceOfTruthEnabled?: boolean | null } | null | undefined,
): boolean {
  return wms?.sourceOfTruthEnabled === true;
}

/** WMS-backed returns processing is enabled by an explicit opt-in or a returns-center location. */
export function isWmsReturnsProcessingEnabled(
  wms: { processingWmsEnabled?: boolean | null } | null | undefined,
  hasReturnsCenter: boolean,
): boolean {
  return wms?.processingWmsEnabled === true || hasReturnsCenter;
}

export function resolveForcedPickSessionStrategy(
  wms:
    | {
        pickSession?: {
          forceMultiItemPickSessions?: boolean | null;
          forceSingleItemPickSessions?: boolean | null;
        } | null;
      }
    | null
    | undefined,
): { forceMultiItem: boolean; forceSingleItem: boolean } {
  const forceMultiItem = wms?.pickSession?.forceMultiItemPickSessions === true;
  const forceSingleItem =
    !forceMultiItem && wms?.pickSession?.forceSingleItemPickSessions === true;
  return { forceMultiItem, forceSingleItem };
}

export function isExactInventoryItemAssignmentEnabled(
  wms:
    | { pickSession?: { exactInventoryItemAssignment?: boolean | null } | null }
    | null
    | undefined,
): boolean {
  return wms?.pickSession?.exactInventoryItemAssignment === true;
}

export const DEFAULT_BIN_COUNT_THRESHOLD_DAYS = 30;

export const TRANSFER_EXCLUDED_BIN_TYPES: readonly StorageLocationType[] = [
  StorageLocationType.EXTERNAL,
  StorageLocationType.LOST,
  StorageLocationType.TRANSIT,
  StorageLocationType.UNMAPPED,
  StorageLocationType.DEFAULT,
];

// Bins holding stock that is on hand and pickable at the warehouse.
export const PICKABLE_BIN_TYPES: readonly StorageLocationType[] = [
  StorageLocationType.BIN,
  StorageLocationType.STORAGE,
  StorageLocationType.RECEIVING,
];

// Transient locations holding stock picked and staged on its way out.
export const PACKED_BIN_TYPES: readonly StorageLocationType[] = [
  StorageLocationType.TOTE,
  StorageLocationType.SORTING_TOTE,
  StorageLocationType.STATION,
];

// Excluded from on-hand inventory in CRDB WMS queries.
export const ON_HAND_EXCLUDED_LOCATION_TYPES: readonly StorageLocationType[] = [
  StorageLocationType.EXTERNAL,
  StorageLocationType.LOST,
  StorageLocationType.FLUSH,
];

// Virtual counterparties: a negative here is bookkeeping, not corruption.
export const NEGATIVE_BIN_EXCLUDED_LOCATION_TYPES: readonly StorageLocationType[] =
  [
    StorageLocationType.EXTERNAL,
    StorageLocationType.LOST,
    StorageLocationType.DEFAULT,
  ];

// The adjustment report's non-shelf set; excludes waste, not flush (WIMS-935).
export const NON_SHELF_LOCATION_TYPES: readonly StorageLocationType[] = [
  StorageLocationType.EXTERNAL,
  StorageLocationType.LOST,
  StorageLocationType.WASTE,
];

// Counterparty types that classify a stock movement as an adjustment.
export const ADJUSTMENT_LOCATION_TYPES: readonly StorageLocationType[] = [
  StorageLocationType.LOST,
  StorageLocationType.WASTE,
];

// Excluded from fulfillable stock (work-order sync, BOM input demand).
export const FULFILLABLE_EXCLUDED_LOCATION_TYPES: readonly StorageLocationType[] =
  [
    StorageLocationType.EXTERNAL,
    StorageLocationType.LOST,
    StorageLocationType.WASTE,
    StorageLocationType.UNMAPPED,
    StorageLocationType.SORTING_TOTE,
    StorageLocationType.STATION,
  ];

// Excluded when sourcing BOM production inputs, outputs, and capacity.
export const BOM_CAPACITY_EXCLUDED_LOCATION_TYPES: readonly StorageLocationType[] =
  [
    StorageLocationType.EXTERNAL,
    StorageLocationType.LOST,
    StorageLocationType.WASTE,
    StorageLocationType.UNMAPPED,
    StorageLocationType.FLUSH,
    StorageLocationType.TRANSIT,
    StorageLocationType.SORTING_TOTE,
    StorageLocationType.STATION,
  ];

// Persistent bins stock can be picked or reserved from. Narrower than
// PICKABLE_BIN_TYPES, which also counts receiving.
export const PICK_SOURCE_LOCATION_TYPES: readonly StorageLocationType[] = [
  StorageLocationType.BIN,
  StorageLocationType.STORAGE,
];

// Sellable source bins for transfers, including location defaults.
export const TRANSFER_SOURCE_LOCATION_TYPES: readonly StorageLocationType[] = [
  StorageLocationType.STORAGE,
  StorageLocationType.BIN,
  StorageLocationType.DEFAULT,
];

export const REPLENISHMENT_SOURCE_LOCATION_TYPES: readonly StorageLocationType[] =
  [StorageLocationType.STORAGE];

export const REPLENISHMENT_UNSUGGESTED_MAX_QUANTITY = 250;

export function isReceivingModeAllowed(
  wms: { receivingMode?: ReceivingMode | null } | null | undefined,
  mode: ReceivingMode.RECEIVE | ReceivingMode.RECEIVE_AND_PLACE,
): boolean {
  const receivingMode = wms?.receivingMode ?? ReceivingMode.BOTH;
  return receivingMode === ReceivingMode.BOTH || receivingMode === mode;
}
